import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { UserOutlined } from "@ant-design/icons";
import { Badge } from "antd";
import CollapseBar from "components/ui/behaviors/CollapseBar";
import { FigmaSvgIcon } from "components/ui/FigmaSvgIcon";
import { TruncatedTooltipText } from "components/ui/typography/TruncatedTooltipText";
import HeaderSideBar from "./HeaderSideBar";
import { useSidebar } from "./useSidebar";
import { fetchRequestCount } from "slices/countSlice";
import { routesPermissions } from "app/routes/routes";
import DownArrowPoint from "components/ui/icons/svgs/DownArrowPoint";

// ——— Inline: SidebarLogo ———
const LOGO_FULL = "/icons/Logo-01 1.svg";
const LOGO_ICON = "/carrumLogos/carrum-icon.svg";

function SidebarLogo({ collapseSidebar, onClick }) {
  const src = collapseSidebar ? LOGO_ICON : LOGO_FULL;
  return (
    <div className="sidebar-logo-container">
      <img
        src={src}
        alt="Company Logo"
        className="h-8 hidden md:block"
        style={{ cursor: "pointer" }}
        onClick={onClick}
      />
    </div>
  );
}

// ——— Inline: SidebarDashboardLink ———
function SidebarDashboardLink({ role, table, collapseSidebar, onDashboardClick }) {
  const canAccessDashboard = routesPermissions?.some(
    (r) => r.path === "dashboard" && (!r.authorized?.length || r.authorized.includes(role))
  );
  if (!canAccessDashboard) return null;

  return (
    <Link
      to="/dashboard/dashboard"
      style={{
        display: "flex",
        marginBottom: "2.4vh",
        textDecoration: "none",
        color: "inherit",
        flexDirection: "row",
        alignItems: "center",
        gap: "7px",
      }}
      className="side-bar-dashboard-text dashboard-sidebar-text cursor-pointer flex flex-row items-center"
      onClick={onDashboardClick}
    >
      <span>
        {" "}
        <DashboardIcon
          size={20}
          color={table === "dashboard" ? "rgb(67,25,255)" : "rgb(126,126,126)"}
          type="dashboard-icon"
        />
      </span>

      <span style={{ display: collapseSidebar ? "none" : null }}>Dashboard</span>
    </Link>
  );
}

const DashboardIcon = ({ color = "currentColor", size = "24" }) => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_1598_29314)">
        <path
          d="M4 13H10C10.55 13 11 12.55 11 12V4C11 3.45 10.55 3 10 3H4C3.45 3 3 3.45 3 4V12C3 12.55 3.45 13 4 13ZM4 21H10C10.55 21 11 20.55 11 20V16C11 15.45 10.55 15 10 15H4C3.45 15 3 15.45 3 16V20C3 20.55 3.45 21 4 21ZM14 21H20C20.55 21 21 20.55 21 20V12C21 11.45 20.55 11 20 11H14C13.45 11 13 11.45 13 12V20C13 20.55 13.45 21 14 21ZM13 4V8C13 8.55 13.45 9 14 9H20C20.55 9 21 8.55 21 8V4C21 3.45 20.55 3 20 3H14C13.45 3 13 3.45 13 4Z"
          fill={color}
        />
      </g>
      <defs>
        <clipPath id="clip0_1598_29314">
          <rect width={size} height={size} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};
// ——— Inline: SidebarMenuItem ———
const DOT_STYLE = {
  minWidth: "15px",
  height: "15px",
  border: "solid",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const ACTIVE_DOT_STYLE = {
  width: "7px",
  height: "7px",
  borderRadius: "50%",
  backgroundColor: "#4318FF",
};

function SidebarMenuItem({ item, isActive, onClick }) {
  return (
    <Link
      to={`/dashboard/${item.sidebarListClick}`}
      style={{ color: "rgba(126, 126, 126, 1)", textDecoration: "none" }}
      className={`sidebar-child-options-container ${
        isActive ? "selected-child-options !hover:bg-[#d0cece]" : ""
      }`}
      onClick={() => onClick(item)}
    >
      <div style={DOT_STYLE}>{isActive && <div style={ACTIVE_DOT_STYLE} />}</div>
      <div className="flex gap-[3px] pr-[1px] w-full justify-between max-w-[100%]">
        <TruncatedTooltipText text={item.text} />
        {item.countBadge && (
          <Badge
            count={item.recordCount}
            overflowCount={99}
            showZero
            color="#f57c00"
            style={{ width: "auto", height: 18, lineHeight: "18px", fontSize: 10 }}
          />
        )}
      </div>
    </Link>
  );
}

// ——— Inline: buildSidebarOptions ———
function buildSidebarOptions({
  sideBarList,
  role,
  table,
  pathname,
  collapseSidebar,
  getDefaultActiveKey,
  setTableParams,
  setSidebarListClick,
  setActiveOptionKey,
  dispatch,
}) {
  return sideBarList?.map((item, index) => ({
    key: item.optionsKey ?? index,
    label: (
      <div style={{ whiteSpace: "nowrap", marginBottom: "2.4vh" }} className="side-bar !pl-0">
        <span className="w-[25px] flex items-center justify-center">
          <FigmaSvgIcon
            color={
              getDefaultActiveKey(table) === (item.optionsKey ?? index)
                ? "rgb(67,25,255)"
                : "rgb(126,126,126)"
            }
            size={item.labelSvgSize}
            type={item.labelSvgType}
          />
        </span>
        <span style={{ display: collapseSidebar ? "none" : null }}>{item.label}</span>
      </div>
    ),
    children: role
      ? item.options?.map((opt) => (
          <SidebarMenuItem
            key={opt.sidebarListClick}
            item={opt}
            isActive={
              opt.sidebarBasePath
                ? pathname === `/dashboard/${opt.sidebarBasePath}` ||
                  pathname.startsWith(`/dashboard/${opt.sidebarBasePath}/`)
                : pathname === `/dashboard/${opt.sidebarListClick}` ||
                  pathname.startsWith(`/dashboard/${opt.sidebarListClick}/`)
            }
            onClick={(clickedItem) => {
              setTableParams(clickedItem.sidebarListClick);
              setSidebarListClick?.(clickedItem.sidebarListClick);
              localStorage.setItem("sidebarListClick", clickedItem.sidebarListClick);
              const route = routesPermissions?.find(
                (r) =>
                  r.path === clickedItem.sidebarListClick ||
                  r.sidebarPath === clickedItem.sidebarListClick
              );
              if (route?.countKey || route?.countKeyAllFleet) dispatch(fetchRequestCount());
            }}
          />
        ))
      : null,
    className: "pl-0",
    style: { margin: "0px", display: item.display },
  }));
}

// ——— Main Sidebar ———
const Sidebar = ({ sidebarListClick = null, setSidebarListClick, collapseSidebar }) => {
  const {
    role,
    userName,
    userEmail,
    table,
    pathname,
    sideBarList,
    activeOptionKey,
    setActiveOptionKey,
    getDefaultActiveKey,
    setTableParams,
    maindashboardClick,
    navigateToDeskPortal,
    showDeskPortalMenu,
    handleLogout,
    dispatch,
  } = useSidebar();

  const sideBarOptions = useMemo(
    () =>
      buildSidebarOptions({
        sideBarList,
        role,
        table,
        pathname,
        collapseSidebar,
        getDefaultActiveKey,
        setTableParams,
        setSidebarListClick,
        setActiveOptionKey,
        dispatch,
      }),
    [
      sideBarList,
      role,
      table,
      pathname,
      collapseSidebar,
      getDefaultActiveKey,
      setTableParams,
      setSidebarListClick,
      setActiveOptionKey,
      dispatch,
    ]
  );

  // Dashboard route has no sidebarGroup so getDefaultActiveKey("dashboard") is undefined; use Control Tower section key.
  const handleDashboardClick = () => {
    setTableParams("dashboard");
    setSidebarListClick?.("dashboard");
    localStorage.setItem("sidebarListClick", "dashboard");
    setActiveOptionKey(getDefaultActiveKey("dashboard") ?? "controlTower");
  };

  return (
    <>
      <SidebarLogo collapseSidebar={collapseSidebar} onClick={maindashboardClick} />

      <div className="invisible-scrollbar sidebar-dashboard-items-container">
        <SidebarDashboardLink
          role={role}
          table={table}
          collapseSidebar={collapseSidebar}
          onDashboardClick={handleDashboardClick}
        />
        <CollapseBar
          expandIcon={({ isActive }) => (
            <span
              className="inline-block transition-transform duration-200"
              style={{ transform: `rotate(${isActive ? 180 : 0}deg)` }}
            >
              <DownArrowPoint color="rgb(126,126,126)" size="20" />
            </span>
          )}
          onChange={(key) => setActiveOptionKey(key?.at(-1))}
          activeKey={!collapseSidebar && activeOptionKey ? [activeOptionKey] : []}
          expandIconPosition="end"
          items={sideBarOptions}
          className="h-full sidebar-collapse"
        />
      </div>

      {collapseSidebar && (
        <img src="/icons/Avatar.svg" alt="Avatar" className="h-[38px] hidden md:block" />
      )}

      {!collapseSidebar && (
        <HeaderSideBar
          className="sidebar-header-container bg-blue-500"
          avatarIcon={<UserOutlined />}
          role={role}
          userName={userName}
          email={userEmail}
          avatarClass="bg-[#87d068]"
          sidebarListClick={sidebarListClick}
          setSidebarListClick={setSidebarListClick}
          handleLogout={handleLogout}
          showDeskPortalMenu={showDeskPortalMenu}
          onNavigateToDeskPortal={navigateToDeskPortal}
        />
      )}
    </>
  );
};

export default Sidebar;
